import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Card, Table, Form, Button, Row, Col, Spinner, InputGroup, Badge } from 'react-bootstrap';
import { FaEye, FaHeadphones } from "react-icons/fa";
import api from '../../service/api';
import { useToast } from '../../contexts/ToastContext';

const AllTelecallers = () => {

  const [telecallers, setTelecallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { showError } = useToast();

  useEffect(() => {
    fetchTelecallers();
  }, []);

  const fetchTelecallers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/users', { params: { limit: 1000 } });
      const allUsers = res.data.users || [];
      const telecallersOnly = allUsers.filter(u => u.role === 'telecaller');
      setTelecallers(telecallersOnly);

    } catch (error) {
      console.error('Telecallers fetch error:', error);
      showError(`Failed to fetch telecallers data: ${error.response?.data?.message || error.message || error.response?.status}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value.toLowerCase());
  };

  const filteredTelecallers = telecallers.filter(u => {
    const matchesSearch = u.username?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search) ||
      u.profile?.firstName?.toLowerCase().includes(search) ||
      u.profile?.lastName?.toLowerCase().includes(search);

    let matchesStatus = true;
    if (statusFilter === 'active') matchesStatus = u.isActive === true;
    if (statusFilter === 'suspended') matchesStatus = u.isActive === false;

    return matchesSearch && matchesStatus;
  });

  const getInitials = (user) => {
    if (user.profile?.firstName && user.profile?.lastName) {
      return (user.profile.firstName[0] + user.profile.lastName[0]).toUpperCase();
    }
    return user.username.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-vh-100 py-4 px-3 bg-light fade-in-up">
      <Container fluid className="mx-auto" style={{ maxWidth: "1280px" }}>

        <div className="bg-white rounded-3 shadow-sm border p-4 mb-4 d-flex justify-content-between align-items-center">
          <div>
            <h1 className="h2 fw-bold text-dark mb-1">All Telecallers</h1>
            <p className="text-secondary mb-0 small">
              View active operators and communication staff
            </p>
          </div>

          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary me-3" style={{ width: "48px", height: "48px" }}>
              <FaHeadphones size={20} />
            </div>
            <div>
              <div className="h4 fw-bold text-dark mb-0">{telecallers.length}</div>
              <div className="text-secondary small fw-bold text-uppercase ls-wider">Active Staff</div>
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
          <Card.Header className="bg-white border-bottom p-4 d-flex gap-3 flex-wrap">
            <InputGroup className="bg-light rounded-3 overflow-hidden border flex-grow-1" style={{ maxWidth: "400px" }}>
              <InputGroup.Text className="bg-light border-0 text-secondary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Search telecallers..."
                className="bg-light border-0 shadow-none"
                value={search}
                onChange={handleSearch}
              />
            </InputGroup>

            <Form.Select
              className="bg-light border shadow-none rounded-3"
              style={{ width: "200px" }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="suspended">Suspended Only</option>
            </Form.Select>
          </Card.Header>

          <div className="table-responsive">
            <Table borderless hover className="mb-0 custom-enterprise-table">
              <thead className="bg-primary bg-opacity-10">
                <tr>
                  <th className="px-4 py-3 text-secondary fw-bold small text-uppercase ls-wider">Operator Profile</th>
                  <th className="px-4 py-3 text-secondary fw-bold small text-uppercase ls-wider">ID</th>
                  <th className="px-4 py-3 text-secondary fw-bold small text-uppercase ls-wider">Contact Email</th>
                  <th className="px-4 py-3 text-secondary fw-bold small text-uppercase ls-wider">Mobile Number</th>
                  <th className="px-4 py-3 text-secondary fw-bold small text-uppercase ls-wider">Status</th>
                  {/* <th className="px-4 py-3 text-secondary fw-bold small text-uppercase ls-wider text-end">Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : filteredTelecallers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-secondary">
                      No telecallers found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredTelecallers.map((u) => (
                    <tr key={u._id} className="hover-row align-middle border-bottom">
                      <td className="px-4 py-3">
                        <div className="d-flex align-items-center gap-3">
                          <div className="rounded-circle bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center"
                            style={{ width: "40px", height: "40px" }}>
                            {getInitials(u)}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{u.username}</div>
                            <div className="text-secondary small">
                              {u.profile?.firstName} {u.profile?.lastName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge bg="dark" className="bg-opacity-10 text-dark rounded-pill px-3 py-1 font-monospace border shadow-xs">
                          {u._id?.slice(-6).toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-secondary">
                        {u.email}
                      </td>
                      <td className="px-4 py-3 text-secondary font-monospace lh-1">
                        {u.profile?.phone || 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge bg={u.isActive ? 'success' : 'danger'} className="text-uppercase px-3 py-1.5 rounded-pill border bg-opacity-10" style={{ color: u.isActive ? '#198754' : '#dc3545', borderColor: u.isActive ? 'rgba(25,135,84,0.2)' : 'rgba(220,53,69,0.2)' }}>
                          {u.isActive ? 'Active' : 'Suspended'}
                        </Badge>
                      </td>
                      {/* <td className="px-4 py-3 text-end">
                        <Link
                          to={`/admin/users/${u._id}`}
                          className="btn btn-outline-primary rounded-circle btn-sm d-inline-flex align-items-center justify-content-center"
                          style={{ width: "36px", height: "36px" }}
                          title="View Profile"
                        >
                          <FaEye />
                        </Link>
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>

      
    </div>
  );
};

export default AllTelecallers;
